import React, { createContext, useContext, useState, useCallback } from 'react';
import { SelfUserProfile } from 'types/user';
import { Role } from 'types/company';
import { update } from 'util/intercom/intercom';

export interface IntercomStore {
  user?: SelfUserProfile;
  company?: {
    name: string;
    id: number;
    isCommunity: boolean;
  };
  roles?: Role[];
  hasBooted: boolean;
}

interface IntercomContextType {
  intercomData: IntercomStore;
  updateUser: (user: SelfUserProfile) => void;
  updateCompany: (company: { id: number; name: string; isCommunity: boolean }, roles: Role[]) => void;
  clearCompany: () => void;
}

const initialState: IntercomStore = { hasBooted: false };

const IntercomContext = createContext<IntercomContextType | undefined>(undefined);

export const useIntercomContext = () => {
  const context = useContext(IntercomContext);
  if (!context) {
    console.error('IntercomContext is undefined. Component may be outside IntercomProvider.');
    console.trace('Context usage stack trace:');
    throw new Error(
      'useIntercomContext must be used within an IntercomProvider. Check that your component is wrapped in <IntercomProvider>',
    );
  }
  return context;
};

export const useIntercom = () => {
  const { intercomData } = useIntercomContext();
  return intercomData;
};

export const useIntercomActions = () => {
  const { updateUser, updateCompany, clearCompany } = useIntercomContext();
  return { updateUser, updateCompany, clearCompany };
};

export const IntercomProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [intercomData, setIntercomData] = useState<IntercomStore>(initialState);

  const updateUser = useCallback((user: SelfUserProfile) => {
    setIntercomData(prevState => {
      const nextState = { ...prevState, user };
      const result = update(nextState);
      return { ...nextState, hasBooted: prevState.hasBooted || result === 'boot' };
    });
  }, []);

  const updateCompany = useCallback((company: { id: number; name: string; isCommunity: boolean }, roles: Role[]) => {
    setIntercomData(prevState => {
      const nextState = {
        ...prevState,
        company,
        roles,
      };
      const result = update(nextState);
      return { ...nextState, hasBooted: prevState.hasBooted || result === 'boot' };
    });
  }, []);

  const clearCompany = useCallback(() => {
    setIntercomData(prevState => ({
      ...prevState,
      company: undefined,
      roles: undefined,
    }));
  }, []);

  return (
    <IntercomContext.Provider
      value={{
        intercomData,
        updateUser,
        updateCompany,
        clearCompany,
      }}
    >
      {children}
    </IntercomContext.Provider>
  );
};
